import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

/**
 * Tiny stale-while-revalidate hook.
 * - Reads localStorage under `cacheKey` and returns it instantly (0 ms perceived load).
 * - If the cached entry is older than `ttlMs` OR missing, silently re-fetches.
 * - Exposes `refetch()` for forced refresh (e.g. a Refresh button).
 *
 * The cache entry shape: `{ data: any, generatedAt: number (epoch ms) }`.
 *
 * Cache keys should include user + language + endpoint, e.g.
 *   `vlg:cache:${user_id}:${language}:ai/action-plan`.
 * Keeps its footprint small — we store JSON strings under a namespaced key.
 */
export const useCachedApi = (cacheKey, path, { ttlMs = 6 * 60 * 60 * 1000 } = {}) => {
    const [data, setData] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    const readCache = useCallback(() => {
        if (!cacheKey || typeof window === "undefined") return null;
        try {
            const raw = window.localStorage.getItem(cacheKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || !parsed.generatedAt) return null;
            return parsed;
        } catch {
            return null;
        }
    }, [cacheKey]);

    const writeCache = useCallback((payload) => {
        if (!cacheKey || typeof window === "undefined") return;
        try {
            window.localStorage.setItem(
                cacheKey,
                JSON.stringify({ data: payload, generatedAt: Date.now() })
            );
        } catch {}
    }, [cacheKey]);

    const fetchFresh = useCallback(async ({ background = false } = {}) => {
        if (background) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const res = await api.get(path);
            if (!mountedRef.current) return;
            setData(res.data);
            const ts = Date.now();
            setGeneratedAt(ts);
            writeCache(res.data);
        } catch (e) {
            if (mountedRef.current) setError(e);
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [path, writeCache]);

    useEffect(() => {
        mountedRef.current = true;
        // Instant hydrate from cache
        const cached = readCache();
        if (cached) {
            setData(cached.data);
            setGeneratedAt(cached.generatedAt);
            setLoading(false);
        }
        // Fetch fresh if missing OR stale
        const isStale = !cached || Date.now() - cached.generatedAt > ttlMs;
        if (isStale) {
            fetchFresh({ background: !!cached });
        }
        return () => { mountedRef.current = false; };
    }, [cacheKey, ttlMs, readCache, fetchFresh]);

    const refetch = useCallback(() => fetchFresh({ background: !!data }), [fetchFresh, data]);

    return { data, generatedAt, loading, refreshing, error, refetch };
};

/** Human-friendly "X ago" from an epoch ms timestamp. */
export const timeAgo = (ts, language = "en") => {
    if (!ts) return language === "es" ? "nunca" : "never";
    const diff = Math.max(0, Date.now() - ts);
    const mins = Math.round(diff / 60000);
    if (mins < 1) return language === "es" ? "hace un momento" : "just now";
    if (mins < 60) return language === "es" ? `hace ${mins} min` : `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return language === "es" ? `hace ${hrs} h` : `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return language === "es" ? `hace ${days} d` : `${days}d ago`;
};

export default useCachedApi;
